import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { Spinner } from '@/components/primitives/Spinner';
import { Icon } from '@/components/Icon';
import { ApiError } from '@/api/client';
import { apiErrorText } from '@/api/opengater/errors';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import { useOgPricing, useUpdateOgPricing } from '@/api/opengater/usePricing';
import { authTypeLabel, useOgAuthMethodWeights, useUpdateOgAuthMethodWeight, weightPatch, } from '@/api/opengater/useAuthMethodWeights';
import { PageTitle } from '@/components/PageTitle';
import { SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, SettingsState, } from '@/components/settings/Settings';
import { ConfigFormScreen } from './ConfigFormScreen';
import w from './OpengaterBonuses.module.css';
const CAP = 'auth-weights.write';
const INT_RE = /^\d+$/;
/**
 * Бонусы — the amounts (users `admin: pricing-settings`) and the share each
 * sign-in method gets of the welcome bonus (GlobalAuth `admin: auth-method-weights`).
 * Two cards, one save each: they are different services.
 */
export function OpengaterBonuses() {
    return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "\u0411\u043E\u043D\u0443\u0441\u044B", sub: "\u0421\u0443\u043C\u043C\u044B \u0431\u043E\u043D\u0443\u0441\u043E\u0432 \u0438 \u0434\u043E\u043B\u044F \u043F\u043E \u0441\u043F\u043E\u0441\u043E\u0431\u0430\u043C \u0432\u0445\u043E\u0434\u0430" }), _jsx(BonusAmounts, {}), _jsx(AuthMethodWeights, {})] }));
}
function BonusAmounts() {
    const q = useOgPricing();
    const upd = useUpdateOgPricing();
    return (_jsx(ConfigFormScreen, { embedded: true, title: "\u0421\u0443\u043C\u043C\u044B", subtitle: "", capability: "pricing.write", sections: [
            {
                title: 'Суммы',
                fields: [
                    {
                        key: 'welcome_gift_bonus',
                        label: 'Приветственный бонус',
                        kind: 'money',
                        suffix: '₽',
                        hint: 'От него считается доля по способу входа.',
                    },
                    {
                        key: 'referred_user_bonus',
                        label: 'Бонус приглашённому',
                        kind: 'money',
                        suffix: '₽',
                    },
                    {
                        key: 'referral_user_bonus',
                        label: 'Бонус пригласившему',
                        kind: 'money',
                        suffix: '₽',
                    },
                ],
            },
        ], data: q.data, isLoading: q.isLoading, error: q.error, saving: upd.isPending, saveError: upd.error, saved: upd.isSuccess, onSave: (patch) => upd.mutate(patch), onResetFeedback: upd.reset }));
}
function AuthMethodWeights() {
    const q = useOgAuthMethodWeights();
    const rows = q.data ?? [];
    if (q.isLoading)
        return _jsx(SettingsLoading, {});
    if (q.error) {
        return (_jsx(SettingsNote, { tone: "error", children: apiErrorText(q.error, 'Не удалось загрузить доли.') }));
    }
    return (_jsxs(SettingsGroup, { title: "\u0414\u043E\u043B\u044F \u043F\u043E \u0441\u043F\u043E\u0441\u043E\u0431\u0443 \u0432\u0445\u043E\u0434\u0430", footer: rows.length > 0 ? '100% — весь приветственный бонус, 0 — без бонуса.' : undefined, children: [rows.length === 0 && _jsx(SettingsState, { children: "\u0421\u043F\u043E\u0441\u043E\u0431\u043E\u0432 \u0432\u0445\u043E\u0434\u0430 \u043D\u0435\u0442" }), rows.map((row) => (_jsx(WeightRow, { row: row }, row.auth_type)))] }));
}
function WeightRow({ row }) {
    const { allowed, markDenied } = useOgAuth();
    const canEdit = allowed(CAP, 'admin');
    const update = useUpdateOgAuthMethodWeight(row.auth_type);
    const [registration, setRegistration] = useState(String(row.registration_weight));
    const [link, setLink] = useState(String(row.link_weight));
    const patch = weightPatch(row, registration, link);
    const busy = !canEdit || update.isPending;
    const save = () => {
        if (!patch)
            return;
        update.mutate(patch, {
            onError: (e) => {
                if (e instanceof ApiError && e.status === 403)
                    markDenied(CAP);
            },
        });
    };
    const error = update.error && !(update.error instanceof ApiError && update.error.status === 403)
        ? apiErrorText(update.error)
        : undefined;
    return (_jsx(SettingsRow, { label: authTypeLabel(row.auth_type), hint: row.updated_at
            ? `Изменено ${new Date(row.updated_at).toLocaleDateString('ru-RU')}`
            : 'По умолчанию', error: error, children: _jsxs("span", { className: w.fields, children: [_jsxs("label", { className: w.field, children: [_jsx("span", { className: w.fieldLbl, children: "\u0417\u0430 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E" }), _jsxs("span", { className: w.pct, children: [_jsx(Input, { value: registration, inputMode: "numeric", mono: true, disabled: busy, onChange: (e) => setRegistration(e.target.value), "aria-invalid": !INT_RE.test(registration.trim()) || undefined }), _jsx("span", { className: w.pctSign, children: "%" })] })] }), _jsxs("label", { className: w.field, children: [_jsx("span", { className: w.fieldLbl, children: "\u0417\u0430 \u043F\u0440\u0438\u0432\u044F\u0437\u043A\u0443" }), _jsxs("span", { className: w.pct, children: [_jsx(Input, { value: link, inputMode: "numeric", mono: true, disabled: busy, onChange: (e) => setLink(e.target.value), "aria-invalid": !INT_RE.test(link.trim()) || undefined }), _jsx("span", { className: w.pctSign, children: "%" })] })] }), canEdit &&
                    (update.isSuccess && !patch ? (_jsxs("span", { className: w.saved, children: [_jsx(Icon, { name: "check", size: 13 }), " \u0421\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u043E"] })) : (_jsx(Button, { onClick: save, disabled: !patch || update.isPending, children: update.isPending ? _jsx(Spinner, { size: 13 }) : 'Сохранить' })))] }) }));
}
